import type { ApiKey } from '../../types';

export function displayName(apiKey: ApiKey): string {
  return apiKey.metadata?.appName || apiKey.publicId;
}

export function formatDate(dateString: string, locale: string): string {
  return new Date(dateString).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatOptionalDate(dateString: string | null | undefined, locale: string): string {
  return dateString ? formatDate(dateString, locale) : '—';
}

export function apiKeyEnvironment(apiKey: ApiKey): string {
  return apiKey.environment || apiKey.metadata?.environment || '—';
}

export function formatRateLimit(apiKey: ApiKey): string {
  const minute = apiKey.rateLimitPerMinute ? `${apiKey.rateLimitPerMinute}/min` : null;
  const day = apiKey.rateLimitPerDay ? `${apiKey.rateLimitPerDay}/day` : null;
  return [minute, day].filter(Boolean).join(' · ') || '—';
}

export type ApiKeySortField =
  | 'name'
  | 'status'
  | 'environment'
  | 'scopes'
  | 'createdAt'
  | 'lastUsed'
  | 'expiresAt';
export type SortDirection = 'asc' | 'desc';

export const API_KEY_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

export function apiKeyScopeCount(apiKey: ApiKey): number {
  return apiKey.scopes?.length ?? apiKey.capabilities.length;
}

export function apiKeySearchText(apiKey: ApiKey): string {
  const scopeText = apiKey.scopes?.map((scope) => scope.scope).join(' ') ?? '';
  const capabilityText = apiKey.capabilities
    .map((capability) => `${capability.capabilityCode} ${capability.capabilityName}`)
    .join(' ');

  return [
    displayName(apiKey),
    apiKey.publicId,
    apiKey.prefix,
    apiKey.status,
    apiKeyEnvironment(apiKey),
    apiKey.metadata?.description,
    scopeText,
    capabilityText,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function apiKeySortValue(apiKey: ApiKey, field: ApiKeySortField): string | number {
  if (field === 'name') return displayName(apiKey).toLowerCase();
  if (field === 'status') return apiKey.status;
  if (field === 'environment') return apiKeyEnvironment(apiKey).toLowerCase();
  if (field === 'scopes') return apiKeyScopeCount(apiKey);
  return apiKey[field] ? new Date(apiKey[field] ?? '').getTime() : 0;
}

export function compareApiKeys(
  left: ApiKey,
  right: ApiKey,
  sortField: ApiKeySortField,
  sortDirection: SortDirection
): number {
  const leftValue = apiKeySortValue(left, sortField);
  const rightValue = apiKeySortValue(right, sortField);
  const direction = sortDirection === 'asc' ? 1 : -1;

  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return (leftValue - rightValue) * direction;
  }

  return String(leftValue).localeCompare(String(rightValue)) * direction;
}
